import React from "react";
import { getMiningAlternative } from "../../../api/getMiningAlternative";
import { MiningAlternativeTypes } from "../../../types/mining/MiningAlternativeTypes";
import { currencyFormatter } from "../../../utils/currencyFormatter";

// Dátum normalizálása: YYYY-MM-DD (nincs locale használat)
const dateOnly = (d: string | Date) => {
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return "-";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Egyszerű fixpontos formázó (nem locale alapú)
const fixed = (v: number | string, digits: number) => {
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n.toFixed(digits) : String(v);
};

export default function MiningalternativePage() {
  const [data, setData] = React.useState<MiningAlternativeTypes | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getMiningAlternative();
        if (active) setData(res);
      } catch (e: any) {
        if (active) setError(e?.message ?? "Adatbetöltési hiba");
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, []);

  const users = data?.userGroupsPurchases ?? [];
  const energy = data?.energyPurchases ?? [];


  // --- Összesítő mutatók (mindkét táblából)
    const totalDeposit = users.reduce((s, u) => s + (u.deposit || 0), 0);
    const totalInvested = energy.reduce((s, e) => s + (e.invested || 0), 0);
    const totalBtcAmount =
    users.reduce((s, u) => s + (u.btcAmount || 0), 0) +
    energy.reduce((s, e) => s + (e.btcAmount || 0), 0);
    const avgBtcPrice = (() => {
    const prices: number[] = [];
    for (const u of users) {
        const n = Number(u.btcPrice);
        if (Number.isFinite(n)) prices.push(n);
    }
    for (const e of energy) {
        const n = Number(e.btcPrice);
        if (Number.isFinite(n)) prices.push(n);
    }
    return prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : NaN;
    })();

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto bg-[#FFF4E1] min-h-screen">{/* krém oldal háttér */}

      {loading && (
        <div className="mt-4 text-[#0B3D91]">Betöltés…</div>
      )}
      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3">{error}</div>
      )}

      {/* Felhasználói tranzakciók — első táblázat */}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="rounded-2xl ring-1 ring-gray-200 shadow bg-[#8B5E3C] p-5">
            <div className="text-xs uppercase font-semibold text-white/80">Átlagos BTC ár (USD)</div>
            <div className="mt-1 text-2xl font-bold text-white">
            {Number.isFinite(avgBtcPrice) ? currencyFormatter(avgBtcPrice, 'USD') : "-"}
            </div>
        </div>

        <div className="rounded-2xl ring-1 ring-gray-200 shadow bg-[#8B5E3C] p-5">
            <div className="text-xs uppercase font-semibold text-white/80">Összes BTC mennyiség</div>
            <div className="mt-1 text-2xl font-bold text-white">
            {fixed(totalBtcAmount, 8)}
            </div>
        </div>

        <div className="rounded-2xl ring-1 ring-gray-200 shadow bg-[#8B5E3C] p-5">
            <div className="text-xs uppercase font-semibold text-white/80">Összes befizetés (USD)</div>
            <div className="mt-1 text-2xl font-bold text-white">
            {currencyFormatter(totalDeposit, 'USD')}
            </div>
        </div>

        <div className="rounded-2xl ring-1 ring-gray-200 shadow bg-[#8B5E3C] p-5">
            <div className="text-xs uppercase font-semibold text-white/80">Összes energiabefizetés (USD)</div>
            <div className="mt-1 text-2xl font-bold text-white">
            {currencyFormatter(totalInvested, 'USD')}
            </div>
        </div>
        </div>

      <section className="mb-10">
        <div className="rounded-2xl shadow ring-1 ring-gray-200 overflow-hidden" style={{ backgroundColor: "#8B5E3C" }}>{/* barna kártya */}
          <div className="p-4 text-white font-semibold">Beutalások</div>
          <div className="bg-white">{/* táblafelület */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-200">{/* szürke fejléc */}
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Felhasználó neve</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Befizetés (USD)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Dátum</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">BTC mennyiség</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">BTC ár (USD)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">Nincs felhasználói tranzakció.</td>
                    </tr>
                  ) : (
                    users.map((u, idx) => (
                      <tr key={`${u.user_id}-${idx}`} className="hover:bg-[#FFF4E1]">{/* krém hover */}
                        <td className="px-4 py-3 text-sm text-gray-900">{u.user?.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{u.deposit}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{dateOnly(u.date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{fixed(u.btcAmount, 8)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{fixed(u.btcPrice, 2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Csoportos tranzakciók — második táblázat */}
      <section>
        <div className="rounded-2xl shadow ring-1 ring-gray-200 overflow-hidden" style={{ backgroundColor: "#8B5E3C" }}>{/* barna kártya */}
          <div className="p-4 text-white font-semibold">Energiabefizetések</div>
          <div className="bg-white">{/* táblafelület */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Csoport neve</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Befektetett összeg (USD)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Dátum</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">BTC mennyiség</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">BTC ár (USD)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {energy.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">Nincs csoportos tranzakció.</td>
                    </tr>
                  ) : (
                    energy.map((e, idx) => (
                      <tr key={`group-${e.group_id}-${idx}`} className="hover:bg-[#FFF4E1]">
                        <td className="px-4 py-3 text-sm text-gray-900">{e.group_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{e.invested}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{dateOnly(e.date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{fixed(e.btcAmount, 8)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{fixed(e.btcPrice, 2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
